/**
 * Keeping a search row with no usable `_id` out of the food search (#6350).
 *
 * Several helpers the search runs on every row read the row's id with no guard: `buildFoodHeader`
 * (through `isLegacyMealId`), `getFoodStatus`, and the source and tier lookups in `rankFoodHeaders`.
 * One row whose `_id` is missing or not text made all of them throw "undefined is not an object
 * (evaluating 'e.startsWith')", and the whole list was replaced by the "couldn't load foods" card.
 *
 * Where such a row comes from is not known yet. So it is dropped from the search, and the caller
 * reports it, naming the read it came from, so its producer can be found and fixed.
 *
 * Imports only pure helpers, so it loads under the node test runner.
 */

import { isUsableDocId } from '@cf-shared/deep-helpers';

/** the rows a search may keep, and the ones it has to leave out because they have no usable id */
export interface RowsByUsableId<T> {
  kept: T[];
  dropped: T[];
}

/** splits search rows into the ones with a usable `_id` and the ones without, keeping their order */
export function splitRowsByUsableId<T extends { _id?: unknown }>(rows: T[]): RowsByUsableId<T> {
  const kept: T[] = [];
  const dropped: T[] = [];
  for (const row of rows) {
    // the same rule the save path uses, so "usable" means one thing everywhere
    if (isUsableDocId(row?._id)) {
      kept.push(row);
    } else {
      dropped.push(row);
    }
  }
  return { kept, dropped };
}

/**
 * What a report may say about a dropped row: the type of its id, its document type, status and
 * revision, and the NAMES of its keys. Never a value: the name and nutrients are user data. A
 * search row built from a food also describes that food (`cachedFood`), because the food is the
 * document whose producer has to be found. Same shape as `describeDroppedBulkRecord` in
 * `database-wrapper-save-recovery.ts`.
 */
export function describeRowWithoutId(row: unknown): Record<string, unknown> {
  if (row === null || typeof row !== 'object') {
    return { rowType: row === null ? 'null' : typeof row };
  }
  const asAny = row as any;
  const description: Record<string, unknown> = {
    idType: typeof asAny._id,
    docType: asAny.type,
    status: asAny.status,
    rev: asAny._rev,
    // key names only - values are user data and must not reach telemetry
    keys: Object.keys(row).sort().join(','),
  };
  if (asAny.cachedFood !== undefined) {
    description.cachedFood = describeRowWithoutId(asAny.cachedFood);
  }
  return description;
}
